import React, { useState, useEffect } from 'react';
import { Lock, Mail, AlertCircle, Key } from 'lucide-react';
import '../styles/Login.css';

function Login({ onLogin }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [licenseKey, setLicenseKey] = useState('');
  const [loading, setLoading] = useState(true);
  const [showLicenseForm, setShowLicenseForm] = useState(false);
  const [licenseError, setLicenseError] = useState('');

  useEffect(() => {
    checkLicenseStatus();
  }, []);

  const checkLicenseStatus = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/license/status');
      const data = await response.json();
      setLicenseStatus(data);
      
      // Show license form if license has expired or trial has ended
      if (!data.isValid || (data.isTrial && data.daysRemaining <= 0)) {
        setShowLicenseForm(true);
        if (data.isTrial && data.daysRemaining <= 0) {
          setError('Your 7-day free trial has expired. Please activate a license key to continue.');
        } else {
          setError('License has expired. Please activate a new license key.');
        }
      }
    } catch (err) {
      setError('Failed to check license: ' + err.message);
      setShowLicenseForm(true);
    } finally {
      setLoading(false);
    }
  };

  const handleActivateLicense = async (e) => {
    e.preventDefault();
    if (!licenseKey.trim()) {
      setLicenseError('Please enter a license key');
      return;
    }

    try {
      setLoading(true);
      
      // Get machine ID first
      const machineRes = await fetch('/api/license/machine-id');
      const machineData = await machineRes.json();
      
      const response = await fetch('/api/license/activate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          licenseKey: licenseKey.trim(),
          machineId: machineData.machineId,
          company: 'Demo Company',
          email: email || 'admin@company.com'
        })
      });

      const data = await response.json();
      if (response.ok && data.isValid) {
        setLicenseStatus(data);
        setShowLicenseForm(false);
        setLicenseError('');
        setLicenseKey('');
      } else {
        setLicenseError(data.message || 'Invalid license key');
      }
    } catch (err) {
      setLicenseError('Error activating license: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Check if license is valid before allowing login
    if (!licenseStatus || !licenseStatus.isValid) {
      setError('License has expired. Please activate a valid license key to continue.');
      setShowLicenseForm(true);
      return;
    }

    // Check if trial has expired
    if (licenseStatus.isTrial && licenseStatus.daysRemaining <= 0) {
      setError('Your 7-day free trial has expired. Please enter a valid license key to continue.');
      setShowLicenseForm(true);
      return;
    }

    if (!email || !password) {
      setError('Please enter email and password');
      return;
    }
    setError('');
    onLogin();
  };

  return (
    <div className="login-wrapper">
      <div className="login-card">
        <div className="login-header">
          <div className="login-logo">
            <img src="/logo.png" alt="SSP Billing" className="login-logo-image" />
          </div>
          <h2>SSP Billing Login</h2>
          <p>Sign in to continue</p>
        </div>

        {/* License Activation Section */}
        {showLicenseForm && (
          <div style={{
            backgroundColor: '#fff3cd',
            border: '1px solid #ffc107',
            borderRadius: '8px',
            padding: '15px',
            marginBottom: '20px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
              <AlertCircle size={20} style={{ color: '#ff6b6b', marginRight: '10px' }} />
              <h3 style={{ margin: 0, color: '#856404' }}>License Required</h3>
            </div>
            <p style={{ margin: '10px 0', color: '#856404', fontSize: '14px' }}>
              You need a valid license key to use this software. Please activate your license below.
            </p>

            {licenseStatus && licenseStatus.isValid && (
              <div style={{
                backgroundColor: licenseStatus.isTrial && licenseStatus.daysRemaining <= 3 ? '#fff3cd' : '#d4edda',
                border: licenseStatus.isTrial && licenseStatus.daysRemaining <= 3 ? '1px solid #ffc107' : '1px solid #28a745',
                borderRadius: '5px',
                padding: '10px',
                marginTop: '10px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <Key size={16} style={{ marginRight: '8px', color: licenseStatus.isTrial && licenseStatus.daysRemaining <= 3 ? '#ff9800' : '#28a745' }} />
                  <div>
                    <div style={{ color: licenseStatus.isTrial && licenseStatus.daysRemaining <= 3 ? '#856404' : '#155724', fontWeight: 'bold' }}>
                      ✓ {licenseStatus.isTrial ? '🎁 FREE TRIAL' : 'License Active'} - {licenseStatus.companyName}
                    </div>
                    <div style={{ color: licenseStatus.isTrial && licenseStatus.daysRemaining <= 3 ? '#856404' : '#155724', fontSize: '12px', marginTop: '3px' }}>
                      {licenseStatus.isTrial ? `${licenseStatus.daysRemaining} days remaining` : `Expires: ${new Date(licenseStatus.expiryDate).toLocaleDateString()}`}
                      {licenseStatus.isTrial && licenseStatus.daysRemaining <= 3 && (
                        <span style={{ display: 'block', color: '#d32f2f', marginTop: '3px', fontWeight: 'bold' }}>
                          ⚠️ Trial expiring soon. Activate a license key to continue.
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {!licenseStatus || !licenseStatus.isValid ? (
              <form onSubmit={handleActivateLicense} style={{ marginTop: '15px' }}>
                {licenseStatus?.isTrial && (
                  <div style={{
                    backgroundColor: '#e3f2fd',
                    border: '1px solid #2196f3',
                    borderRadius: '5px',
                    padding: '10px',
                    marginBottom: '10px',
                    fontSize: '12px',
                    color: '#1565c0'
                  }}>
                    ℹ️ You are currently using a <strong>7-day free trial</strong>. 
                    To continue after the trial expires, please enter a valid license key below.
                  </div>
                )}
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '12px', fontWeight: 'bold', color: '#333' }}>License Key</label>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    backgroundColor: '#fff',
                    border: '1px solid #ddd',
                    borderRadius: '5px',
                    padding: '8px'
                  }}>
                    <Key size={16} style={{ color: '#666', marginRight: '8px' }} />
                    <input
                      type="text"
                      value={licenseKey}
                      onChange={(e) => setLicenseKey(e.target.value)}
                      placeholder="Enter your license key"
                      style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                {licenseError && (
                  <div style={{
                    backgroundColor: '#f8d7da',
                    border: '1px solid #f5c6cb',
                    color: '#721c24',
                    padding: '8px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    marginBottom: '10px'
                  }}>
                    {licenseError}
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  style={{
                    width: '100%',
                    padding: '10px',
                    backgroundColor: '#ffc107',
                    color: '#333',
                    border: 'none',
                    borderRadius: '5px',
                    fontWeight: 'bold',
                    cursor: loading ? 'not-allowed' : 'pointer',
                    opacity: loading ? 0.6 : 1
                  }}
                >
                  {loading ? 'Activating...' : 'Activate License'}
                </button>
              </form>
            ) : (
              <button
                onClick={() => setShowLicenseForm(false)}
                style={{
                  width: '100%',
                  padding: '10px',
                  marginTop: '15px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  fontWeight: 'bold',
                  cursor: 'pointer'
                }}
              >
                Continue to Login
              </button>
            )}
          </div>
        )}

        {/* Login Form - Only show if license is valid */}
        {licenseStatus && licenseStatus.isValid && (
          <>
            {/* License Info Banner - Show while logging in */}
            <div style={{
              backgroundColor: '#d4edda',
              border: '1px solid #28a745',
              borderRadius: '8px',
              padding: '12px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between'
            }}>
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Key size={18} style={{ marginRight: '10px', color: '#28a745' }} />
                <div>
                  <div style={{ color: '#155724', fontWeight: 'bold', fontSize: '14px' }}>
                    ✓ License Active
                  </div>
                  <div style={{ color: '#155724', fontSize: '12px' }}>
                    {licenseStatus.companyName}
                    {licenseStatus.expiryDate && ` • Expires: ${new Date(licenseStatus.expiryDate).toLocaleDateString()}`}
                  </div>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="login-form">
              <label className="form-label">Email</label>
              <div className="input-group">
                <Mail size={16} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>

              <label className="form-label">Password</label>
              <div className="input-group">
                <Lock size={16} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter password"
                />
              </div>

              {error && <div className="form-error">{error}</div>}

              <button type="submit" className="login-btn">Login</button>
            </form>
          </>
        )}

        {loading && (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>Checking license...</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default Login;
